import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { superadminInvoicesApi, superadminSchoolsApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Plus,
  Download,
  Trash2,
  Loader2,
  Receipt,
  CheckCircle2,
  Clock,
  IndianRupee,
} from 'lucide-react';

const STATUS_BADGE = {
  paid: 'bg-green-500/10 text-green-600 border-green-500/20',
  unpaid: 'bg-red-500/10 text-red-600 border-red-500/20',
  cancelled: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

export default function SuperAdminInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    school_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: '',
    items: [{ description: '', quantity: 1, rate: '', hsn_sac: '' }],
    gst_enabled: false,
    gst_rate: 18,
    gst_intra_state: true,
    vendor_gstin: '',
    school_gstin: '',
    place_of_supply: '',
  });

  // Mark-paid dialog
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [paidInvoiceId, setPaidInvoiceId] = useState(null);
  const [paidForm, setPaidForm] = useState({
    paid_date: new Date().toISOString().split('T')[0],
    payment_mode: 'upi',
    transaction_id: '',
  });

  useEffect(() => {
    load();
  }, []);

  const [filterSchoolId, setFilterSchoolId] = useState('');
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filterSchoolId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = filterSchoolId ? { school_id: filterSchoolId } : {};
      const [invRes, schRes] = await Promise.all([
        superadminInvoicesApi.list(params),
        superadminSchoolsApi.list(),
      ]);
      setInvoices(invRes.data.invoices || []);
      setSummary(invRes.data.summary || null);
      setSchools(schRes.data.schools || []);
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = form.items.reduce(
    (s, it) => s + (parseFloat(it.rate || 0) * parseFloat(it.quantity || 0)),
    0
  );

  const addItem = () =>
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, rate: '', hsn_sac: '' }] });
  const removeItem = (i) =>
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, k, v) => {
    const next = [...form.items];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, items: next });
  };

  const handleCreate = async () => {
    if (!form.school_id) return toast.error('Select a school');
    const items = form.items
      .filter((it) => it.description && parseFloat(it.rate) > 0)
      .map((it) => ({
        description: it.description,
        quantity: parseFloat(it.quantity || 1),
        rate: parseFloat(it.rate),
        hsn_sac: it.hsn_sac || null,
      }));
    if (items.length === 0) return toast.error('Add at least one line item');

    setSaving(true);
    try {
      const res = await superadminInvoicesApi.create({
        school_id: form.school_id,
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        notes: form.notes || null,
        items,
        gst_enabled: !!form.gst_enabled,
        gst_rate: parseFloat(form.gst_rate) || 18,
        gst_intra_state: !!form.gst_intra_state,
        vendor_gstin: form.vendor_gstin || null,
        school_gstin: form.school_gstin || null,
        place_of_supply: form.place_of_supply || null,
      });
      toast.success(`Invoice ${res.data.invoice_number} created`);
      window.open(superadminInvoicesApi.pdfUrl(res.data.id), '_blank', 'noopener,noreferrer');
      setOpen(false);
      setForm({
        school_id: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        notes: '',
        items: [{ description: '', quantity: 1, rate: '', hsn_sac: '' }],
        gst_enabled: false,
        gst_rate: 18,
        gst_intra_state: true,
        vendor_gstin: '',
        school_gstin: '',
        place_of_supply: '',
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await superadminInvoicesApi.delete(id);
      toast.success('Invoice deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const openMarkPaid = (id) => {
    setPaidInvoiceId(id);
    setPaidForm({
      paid_date: new Date().toISOString().split('T')[0],
      payment_mode: 'upi',
      transaction_id: '',
    });
    setPaidDialogOpen(true);
  };

  const handleMarkPaid = async () => {
    try {
      await superadminInvoicesApi.update(paidInvoiceId, {
        status: 'paid',
        paid_date: paidForm.paid_date,
        payment_mode: paidForm.payment_mode,
        transaction_id: paidForm.transaction_id || null,
      });
      toast.success('Marked as paid');
      setPaidDialogOpen(false);
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6" data-testid="superadmin-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <Receipt className="h-7 w-7 text-primary" /> Vendor Invoices
          </h1>
          <p className="text-muted-foreground mt-1">
            Bills raised by EduTrack to schools — for development, hosting,
            customisation and recurring services.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-invoice-button">
          <Plus className="h-4 w-4 mr-2" />
          New Invoice
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          icon={Receipt}
          label="Total Invoiced"
          value={`Rs. ${(summary?.total_invoiced || 0).toLocaleString()}`}
          tint="text-blue-600"
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Paid"
          value={`Rs. ${(summary?.total_paid || 0).toLocaleString()}`}
          tint="text-green-600"
        />
        <SummaryCard
          icon={Clock}
          label="Pending"
          value={`Rs. ${(summary?.total_pending || 0).toLocaleString()}`}
          tint="text-amber-600"
        />
        <SummaryCard
          icon={IndianRupee}
          label="Invoices"
          value={summary?.count || 0}
          tint="text-primary"
        />
      </div>

      <Card className="border border-border">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle>
              {filterSchoolId
                ? `Invoices for ${schools.find((s) => s.id === filterSchoolId)?.name || ''}`
                : 'All invoices'}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select
                value={filterSchoolId || 'all'}
                onValueChange={(v) => setFilterSchoolId(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="w-64" data-testid="invoice-filter-school-select">
                  <SelectValue placeholder="Filter by school" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All schools</SelectItem>
                  {schools.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} {s.code ? `(${s.code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <a
                href={superadminInvoicesApi.exportUrl(null, filterSchoolId || null)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="export-invoices-button"
              >
                <Button variant="outline">Export Excel</Button>
              </a>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              No invoices yet. Click <b>New Invoice</b> to create your first.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                      <TableCell>{inv.invoice_date}</TableCell>
                      <TableCell>
                        <div className="font-medium">{inv.school_name}</div>
                        <div className="text-xs text-muted-foreground">{inv.school_code}</div>
                      </TableCell>
                      <TableCell className="font-medium">
                        Rs. {(inv.total || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGE[inv.status] || STATUS_BADGE.unpaid}>
                          {inv.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <a
                          href={superadminInvoicesApi.pdfUrl(inv.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`download-invoice-${inv.id}`}
                        >
                          <Button size="sm" variant="outline">
                            <Download className="h-4 w-4" />
                          </Button>
                        </a>
                        {inv.status !== 'paid' && (
                          <Button
                            size="sm"
                            onClick={() => openMarkPaid(inv.id)}
                            data-testid={`mark-paid-${inv.id}`}
                          >
                            Mark Paid
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(inv.id)}
                          data-testid={`delete-invoice-${inv.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1">
                <Label>Bill to (School) *</Label>
                <Select
                  value={form.school_id}
                  onValueChange={(v) => setForm({ ...form, school_id: v })}
                >
                  <SelectTrigger data-testid="invoice-school-select">
                    <SelectValue placeholder="Select school" />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.code} — {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Invoice Date</Label>
                <Input
                  type="date"
                  value={form.invoice_date}
                  onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Line items</Label>
              <div className="space-y-2 mt-1">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-7">
                      <Input
                        placeholder="Description (e.g. Development charges)"
                        value={it.description}
                        onChange={(e) => updateItem(i, 'description', e.target.value)}
                        data-testid={`invoice-item-desc-${i}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={it.quantity}
                        onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={it.rate}
                        onChange={(e) => updateItem(i, 'rate', e.target.value)}
                        data-testid={`invoice-item-rate-${i}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={form.items.length <= 1}
                        onClick={() => removeItem(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-2">
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add line
                </Button>
                <p className="text-sm font-medium">
                  Total: <span className="text-primary">Rs. {totalAmount.toLocaleString()}</span>
                </p>
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Initial setup, AY 2026-27..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="create-invoice-submit">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create & Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark paid dialog */}
      <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Paid Date</Label>
              <Input
                type="date"
                value={paidForm.paid_date}
                onChange={(e) => setPaidForm({ ...paidForm, paid_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Payment Mode</Label>
              <Select
                value={paidForm.payment_mode}
                onValueChange={(v) => setPaidForm({ ...paidForm, payment_mode: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="online">Bank transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transaction ID / UTR / Cheque No.</Label>
              <Input
                value={paidForm.transaction_id}
                onChange={(e) => setPaidForm({ ...paidForm, transaction_id: e.target.value })}
                placeholder="optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMarkPaid} data-testid="confirm-mark-paid">Mark Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SummaryCard = ({ icon: Icon, label, value, tint }) => (
  <Card className="border border-border">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`p-2 rounded-md bg-muted ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold">{value}</p>
      </div>
    </CardContent>
  </Card>
);
